import * as React from 'react'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as FamilleStore from '@store/famille/famille'
import * as AuthStore from '@store/auth/auth'
import * as UserStore from '@store/user/user'
import * as LocationStore from '@store/location/location'

import { findFamilyParents, findLocalisationParents } from '@screens/incident/utils'

import { START, PRESTATION, INFRA, END, getStarter } from './workflow'
import { AuthScope } from '@api/enum'

interface WorkflowOptions {
  withManifestationId?: string
  withEDLId?: string
  withSpaceId?: string
  withIncidentToCopy?: IncidentResponse
  mode?: 'edit'
  positionX?: string
  positionY?: string
}

const buildWorkflow = (form: IncidentForm) => {
  const ACTION = form.isInfrastructure === 'INFRA' ? INFRA : PRESTATION
  return [...START, ...ACTION, ...END]
}

const prestaRules = (incident: IncidentResponse, familles: FamilleResponse[]): Partial<IncidentForm> => {
  if (incident.isInfrastructure || !incident.commandeAssociee) {
    return {}
  }
  const commande = incident.commandeAssociee

  const selectedFamille = familles.find((f) => f.id === incident.famillePrestationId)
  const parents = selectedFamille ? findFamilyParents(familles, selectedFamille) : []

  return {
    enseigneCommande: commande.enseigne,
    isCommandeOrganisateur: commande.isOrganisateur,
    numeroCommande: commande.numeroCommande,
    numeroLigne: commande.numeroLigne,
    referenceArticleCommande: commande.referenceArticle,
    familles: parents.map((f) => f.id),
    famillesMinEditable: parents.length,
    alleeCommande: commande.allee,
    standCommande: commande.stand,
    espaceCommande: commande.espace,
    libelleFamille: commande.libelleFamille,
    libelleSousFamille: commande.libelleSousFamille,
    libelleArticle: commande.libelleArticle,
    isInfrastructure: 'PRESTA',
    prestation: incident.famillePrestationId,

    // Pas possible de les récupérer sans la commande originale
    // enseigneMail: string
    // enseigneTelephone?: string
    // enseigneNomContact: string
  }
}

const infraRules = (
  incident: IncidentResponse,
  familles: FamilleResponse[],
  places: LocalisationResponse[]
): Partial<IncidentForm> => {
  if (!incident.isInfrastructure || !incident.famillePrestationId || !incident.localisationId) {
    return {}
  }

  const famille = familles.find((f) => f.id === incident.famillePrestationId)
  const tree = famille ? findFamilyParents(familles, famille) : []

  const loc = places.find((pl) => pl.id === incident.localisationId)
  const locs = loc ? findLocalisationParents(places, loc).map((l) => l.id) : []

  return {
    isInfrastructure: 'INFRA',

    familles: tree.map((f) => f.id),
    famillesDeepLength: tree.length - 1,
    isRemiseEnEtat: tree[0].isRemiseEnEtat,

    space: locs[0],
    location: locs[1],
    subLocation: locs[2],
    subsubLocation: locs[3],
  }
}

const useWorkflow = (form: IncidentForm, options: WorkflowOptions) => {
  const site = useReducer(SiteStore.store, (s) => s.site)
  const sites = useReducer(SiteStore.store, (s) => s.sites)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const familles = useReducer(FamilleStore.store, (s) => s.activities)
  const places = useReducer(LocationStore.store, (s) => s.places)

  const scopes = useReducer(AuthStore.store, (s) => s.scopes)
  const user = useReducer(UserStore.store, (s) => s.user)

  const [workflow, setWorkflow] = React.useState(buildWorkflow(form))
  const [starter, setStarter] = React.useState(getStarter(scopes))

  const INC_ACTIVITY = React.useMemo(() => familles.find((a) => !a.parentIdFamille && !a.isRemiseEnEtat), [familles])
  const REE_ACTIVITY = React.useMemo(() => familles.find((a) => !a.parentIdFamille && a.isRemiseEnEtat), [familles])

  React.useEffect(() => {
    const next = buildWorkflow(form)

    // add rules on workflow
    // every rule defined workflow cut

    // *************************************************
    //                  content rules
    // *************************************************

    // only 1 site available or selected before -> skip site selection for CONNECTED users and START for NOT CONNECTED
    const RuleSite = sites.length === 1 || site ? ['SITE', 'SITE_EXPO'] : ['START']

    // for any not connected user skip start
    const RuleStartExpo = !user ? ['START'] : []

    // for any connected user skip expo command
    const RuleCommandExpo = !!user ? ['COMMAND_SEARCH_EXPO', 'COMMAND_EXPO'] : []

    // for any connected user skip expo manif else skip manif, same for site
    const RuleManifExpo = !!user ? ['SITE_EXPO', 'MANIF_EXPO'] : ['SITE', 'MANIF']

    // *************************************************
    //                  scopes rules
    // *************************************************

    // only 1 manif available -> skip manif selection for roles without empty choice
    const RuleManifestation =
      manifestations.inProgress.length === 1 && !scopes.includes(AuthScope.DemandesHorsManifestationCreation)
        ? ['MANIF', 'MANIF_EXPO']
        : []

    // no right "create prestation as exposant" -> skip enseigne selection
    const RuleEnseigne = !scopes.includes(AuthScope.DemandesPrestationExposantCreation) ? ['COMMAND_SEARCH'] : []

    // no right "select command" -> skip command selection
    const RuleCommand =
      !scopes.includes(AuthScope.PrestationsCommandesExposantLectures) &&
      !scopes.includes(AuthScope.PrestationsCommandesOrganisateurLectures)
        ? ['COMMAND']
        : []

    // no right "create prestation" or "create infra" -> skip type selection
    const RuleType =
      (!scopes.includes(AuthScope.DemandesRemiseEnEtatCreation) &&
        !scopes.includes(AuthScope.DemandesIncidentCreation)) ||
      (!scopes.includes(AuthScope.DemandesPrestationExposantCreation) &&
        !scopes.includes(AuthScope.DemandesPrestationOrganisateurCreation))
        ? ['TYPE']
        : []

    // no right "affect prestataire" -> skip prestataire selection
    const RulePrestataire = !scopes.includes(AuthScope.DemandesPrestataireAffectation) ? ['PRESTA'] : []

    // no right "declare tiers" -> skip contact info
    const RuleOtherUser = !!user
      ? !scopes.includes(AuthScope.DemandesContactCreation)
        ? ['USER', 'USER_DETAIL']
        : []
      : ['USER']

    const RuleUserDetail = !!user && form.user === 'me' ? ['USER_DETAIL'] : []

    // *************************************************
    //                  options rules
    // *************************************************

    // option withManifestationId -> skip start and manifestation selection
    const RuleOptionManifestation = options.withManifestationId ? ['START', 'MANIF', 'MANIF_EXPO'] : []

    // option withEDLId -> skip type selection (force INFRA)
    const RuleOptionEDL = options.withEDLId ? ['TYPE'] : []

    // option withSpaceId -> skip space selection
    const RuleOptionSpace = options.withSpaceId ? ['POSITION'] : []

    // option position -> skip space selection
    const RuleOptionPosition = options.withSpaceId ? ['MAP'] : []

    //combined every rules
    const wf = next
      .filter((s) => !RuleManifestation.includes(s))
      .filter((s) => !RuleSite.includes(s))
      .filter((s) => !RuleStartExpo.includes(s))
      .filter((s) => !RuleCommandExpo.includes(s))
      .filter((s) => !RuleManifExpo.includes(s))
      .filter((s) => !RuleEnseigne.includes(s))
      .filter((s) => !RuleCommand.includes(s))
      .filter((s) => !RuleType.includes(s))
      .filter((s) => !RulePrestataire.includes(s))
      .filter((s) => !RuleOtherUser.includes(s))
      .filter((s) => !RuleUserDetail.includes(s))
      .filter((s) => !RuleOptionManifestation.includes(s))
      .filter((s) => !RuleOptionEDL.includes(s))
      .filter((s) => !RuleOptionSpace.includes(s))
      .filter((s) => !RuleOptionPosition.includes(s))

    setWorkflow(wf)
  }, [scopes, form, manifestations, sites, options.withManifestationId, options.withEDLId, options.withSpaceId])

  React.useEffect(() => {
    const next = getStarter(scopes)

    // add rules on form starter
    // every rule defined starter adds

    // site affected to user -> select this site
    const RuleUserSite: Partial<IncidentForm> = site ? { siteId: site.id } : {}

    // only 1 site available -> select auto on this site
    const RuleSite: Partial<IncidentForm> = sites.length === 1 ? { siteId: sites[0].id } : {}

    // only 1 manif available -> select auto on this manif
    const RuleManifestation: Partial<IncidentForm> =
      manifestations.inProgress.length === 1
        ? !options.withSpaceId
          ? { manifestationId: manifestations.inProgress[0].id }
          : manifestations.inProgress[0].espaceIds.includes(options.withSpaceId)
          ? { manifestationId: manifestations.inProgress[0].id }
          : { manifestationId: undefined }
        : {}

    // not connected -> select preconfig
    const RuleUserConnected: Partial<IncidentForm> = !user ? { isInfrastructure: 'PRESTA', isRemiseEnEtat: false } : {}

    // *************************************************
    //                  scopes rules
    // *************************************************

    // no right "create prestation" or "create infra" -> select other activities
    const RuleType: Partial<IncidentForm> =
      scopes.includes(AuthScope.DemandesRemiseEnEtatCreation) && REE_ACTIVITY
        ? scopes.includes(AuthScope.DemandesIncidentCreation) && INC_ACTIVITY
          ? {} // can create both - will not preregister
          : { familles: [REE_ACTIVITY.id], famillesMinEditable: 1, famillesDeepLength: 1 } // can create remise-en-etat but not incident
        : scopes.includes(AuthScope.DemandesIncidentCreation) && INC_ACTIVITY
        ? { familles: [INC_ACTIVITY.id], famillesMinEditable: 1, famillesDeepLength: 1 } // can create incident but not remise-en-etat
        : {} // no rights - will not preregister

    // *************************************************
    //                  options rules
    // *************************************************

    // option withManifestationId -> set manifestationId
    const RuleOptionManifestation: Partial<IncidentForm> = options.withManifestationId
      ? { manifestationId: options.withManifestationId }
      : {}

    // option withEDLId -> set INFRA, set edl to true
    const RuleOptionEDL: Partial<IncidentForm> = options.withEDLId
      ? { isInfrastructure: 'INFRA', edl: true, edlId: options.withEDLId }
      : {}

    // option withSpaceId -> set space
    const RuleOptionSpace: Partial<IncidentForm> = options.withSpaceId
      ? { space: options.withSpaceId, isInfrastructure: 'INFRA' }
      : {}

    // option with poitions x & y -> set position on map
    const RuleOptionPosition: Partial<IncidentForm> =
      options.positionX && options.positionY ? { plan: { x: options.positionX, y: options.positionY } } : {}

    //combined every rules
    const st = {
      ...next,
      ...RuleUserSite,
      ...RuleSite,
      ...RuleManifestation,
      ...RuleType,
      ...RuleOptionManifestation,
      ...RuleUserConnected,
      ...RuleOptionEDL,
      ...RuleOptionSpace,
      ...RuleOptionPosition,
    }

    const RuleOptionIncidentToCopy: Partial<IncidentForm> = !!options.withIncidentToCopy
      ? {
          // MANIFESTATION
          // use manifestation from incident if available (inProgress + actif), else horsManif
          manifestationId: manifestations.inProgress
            .filter((m) => m.actif)
            .find((m) => m.id === options.withIncidentToCopy!.manifestationId)?.id,

          // CONTACT
          nomContact: options.withIncidentToCopy.contactAssocie?.nom,
          prenomContact: options.withIncidentToCopy.contactAssocie?.prenom,
          emailContact: options.withIncidentToCopy.contactAssocie?.email,
          telephoneContact: options.withIncidentToCopy.contactAssocie?.telephone,
          user: !user || user.email === options.withIncidentToCopy.contactAssocie?.email ? 'me' : 'other',

          // COMMANDE
          ...prestaRules(options.withIncidentToCopy, familles),

          // FAMILLE + LOCALISATION
          ...infraRules(options.withIncidentToCopy, familles, places),

          // OTHER
          commentaire: undefined,
          photos: [],
          commandSearch: 'location',
          plan:
            options.withIncidentToCopy.plan && options.withIncidentToCopy.plan.x
              ? options.withIncidentToCopy.plan
              : undefined,
          remonteDansEtatDesLieux: options.withIncidentToCopy.remonteDansEtatDesLieux,
          isPrioritaire: options.withIncidentToCopy.isPrioritaire,
          quantite: options.withIncidentToCopy.quantite,

          societePrestataireId: options.withIncidentToCopy.societePrestataireId,

          step: 'RECAP',
        }
      : {}

    const final = { ...st, ...RuleOptionIncidentToCopy }

    setStarter(final)
  }, [scopes, manifestations, sites, site, user, options.withIncidentToCopy, options.positionX, options.positionY])

  return [workflow, starter, options] as const
}

export default useWorkflow
